import { ArticleActionToolbar } from "~/components/ArticleActionToolbar";
import { ArticleAuthorStack } from "~/components/ArticleAuthorStack";
import {
  apiToArticleBylineProps,
  ArticleByline,
} from "~/components/ArticleByline";
import {
  apiToArticleFeaturedImageProps,
  ArticleFeaturedImage,
} from "~/components/ArticleFeaturedImage";
import {
  apiToArticleHeaderProps,
  ArticleHeader,
} from "~/components/ArticleHeader";
import { apiToTagSubscriptionsProps } from "~/components/ArticleTagSubscriptions";
import { AssetDownload, AssetDownloadProps } from "~/components/AssetDownload";
import { apiToAuthorBioProps, AuthorBio } from "~/components/AuthorBio";
import { ContentPromo, ContentPromoProps } from "~/components/ContentPromo";
import { EvervizChart, EvervizChartProps } from "~/components/EvervizChart";
import {
  apiArticleToKeyTakeawaysCardProps,
  KeyTakeawaysCard,
} from "~/components/KeyTakeawaysCard";
import { LandingPageContainer } from "~/components/LandingPageV2/components/LandingPageContainer";
import { LandingPageContent } from "~/components/LandingPageV2/components/LandingPageContent";
import { GatedContent } from "~/components/Piano";
import {
  PostContentBlocks,
  PostContentBlocksProps,
} from "~/components/PostContentBlocks";
import { PostEditCTA, PostEditCTAProps } from "~/components/PostEditCTA";
import {
  apiToSeriesIntroductionCardProps,
  SeriesIntroductionCard,
} from "~/components/SeriesIntroductionCard";
import { Sidecar } from "~/components/Sidecar/Sidecar";
import { SidecarBlock } from "~/components/Sidecar/SidecarBlock";
import { useSidecarProps } from "~/components/Sidecar/useSidecarProps";
import {
  StickyNewsletterFlyout,
  StickyNewsletterProps,
  useStickyNewsletterFlyout,
} from "~/components/StickyNewsletter";
import { Tags, TagsProps } from "~/components/Tags";
import { Headline } from "~/ui-library";

export type ArticlePageProps = {
  assetDownloadProps: AssetDownloadProps | null;
  articleHeaderProps: ReturnType<typeof apiToArticleHeaderProps>;
  articleFeaturedImageProps: ReturnType<typeof apiToArticleFeaturedImageProps>;
  articleBylineProps: ReturnType<typeof apiToArticleBylineProps>;
  authorBioProps: ReturnType<typeof apiToAuthorBioProps>;
  headerChartProps: EvervizChartProps | null;
  keyTakeawaysCardProps: ReturnType<typeof apiArticleToKeyTakeawaysCardProps>;
  postContentBlocksProps: PostContentBlocksProps;
  contentPromoProps: Pick<ContentPromoProps, "posts"> | null;
  seriesIntroductionCardProps: ReturnType<
    typeof apiToSeriesIntroductionCardProps
  >;
  tagsProps: TagsProps | null;
  tagSubscriptionsProps: ReturnType<typeof apiToTagSubscriptionsProps>;
  shareUrl: string;
  postId: number;
  /** When this is populated, show a sticky newsletter signup on the page */
  stickyNewsletterProps: StickyNewsletterProps | null;
  postEditCTAProps: PostEditCTAProps;
  enableSidecar: boolean;
  preview: boolean;
};

export const ArticlePage: React.FC<ArticlePageProps> = ({
  assetDownloadProps,
  articleHeaderProps,
  articleFeaturedImageProps,
  articleBylineProps,
  seriesIntroductionCardProps,
  headerChartProps,
  keyTakeawaysCardProps,
  postContentBlocksProps,
  authorBioProps,
  tagsProps,
  shareUrl,
  postId,
  contentPromoProps,
  stickyNewsletterProps,
  postEditCTAProps,
  enableSidecar,
  preview,
}) => {
  const { closeFlyout, isShowingFlyout, session, status } =
    useStickyNewsletterFlyout(stickyNewsletterProps?.newsletterSlug);

  const {
    isLoadingSidecar,
    isValidatingSidecar,
    showDesktopSidecar,
    sidecarBlocks,
    keyTopicsBlock,
    postListBlocks,
    relatedReportBlock,
    wysiwygBlocks,
  } = useSidecarProps({ postId, preview });

  return (
    <>
      <PostEditCTA {...postEditCTAProps} />

      <article className="bg-white pb-xl">
        {/* No margin, rely on child below to space itself from the header */}
        <div className="mx-auto w-full">
          {/* istanbul ignore next */}
          <LandingPageContent maxWidth="medium">
            <ArticleHeader {...articleHeaderProps} />
          </LandingPageContent>
        </div>
        <LandingPageContainer
          className="md:max-w-[1024px]"
          sidecar={
            enableSidecar &&
            showDesktopSidecar && (
              <Sidecar
                isLoading={isLoadingSidecar}
                isValidating={isValidatingSidecar}
                blocks={sidecarBlocks}
                data-testid="article-sidecar"
              />
            )
          }
          topPadding="none"
        >
          {headerChartProps ? (
            <LandingPageContent
              as="section"
              maxWidth={enableSidecar ? "memo" : "medium"}
              className="mb-l mt-m"
              data-testid="header-chart"
            >
              <EvervizChart
                {...headerChartProps}
                className="mb-l mt-s"
                key={postId}
              />
            </LandingPageContent>
          ) : null}
          {articleFeaturedImageProps ? (
            <LandingPageContent
              as="section"
              maxWidth={enableSidecar ? "memo" : "medium"}
              data-testid="featured-image"
            >
              <ArticleFeaturedImage
                {...articleFeaturedImageProps}
                className="mb-l"
              />
            </LandingPageContent>
          ) : null}
          <LandingPageContent
            as="section"
            maxWidth={enableSidecar ? "memo" : "medium"}
            data-testid="byline-and-actions"
          >
            {articleBylineProps && <ArticleByline {...articleBylineProps} />}
            <ArticleActionToolbar
              showBookmark={false}
              showDownload={false}
              shareTitle={articleHeaderProps.headline}
              shareUrl={shareUrl}
            />
          </LandingPageContent>
          {seriesIntroductionCardProps ? (
            <LandingPageContent
              as="section"
              maxWidth={enableSidecar ? "memo" : "medium"}
              className={"my-l"}
              data-testid="series-introduction"
            >
              <SeriesIntroductionCard {...seriesIntroductionCardProps} />
            </LandingPageContent>
          ) : null}
          {keyTakeawaysCardProps ? (
            <LandingPageContent
              as="section"
              maxWidth={enableSidecar ? "memo" : "medium"}
              className="my-l"
              data-testid="takeaways"
            >
              <KeyTakeawaysCard {...keyTakeawaysCardProps} />
            </LandingPageContent>
          ) : null}
          {!!(enableSidecar && !showDesktopSidecar && relatedReportBlock) && (
            <LandingPageContent
              as="section"
              maxWidth="memo"
              className="my-l"
              data-testid="sidecar-related-report-mobile"
            >
              <Sidecar
                isLoading={isLoadingSidecar}
                isValidating={isValidatingSidecar}
                blocks={[relatedReportBlock]}
              />
            </LandingPageContent>
          )}
          {assetDownloadProps ? (
            <LandingPageContent
              as="section"
              maxWidth={enableSidecar ? "memo" : "medium"}
              className="my-xl print:hidden"
              data-testid="asset-download"
            >
              <AssetDownload {...assetDownloadProps} />
            </LandingPageContent>
          ) : null}
          <GatedContent>
            <PostContentBlocks
              {...postContentBlocksProps}
              maxColumnWidth={enableSidecar ? "memo" : "medium"}
            />
            {/* Clearfix - If any floating content sits at the end of the content blocks, prevent other content from moving up */}
            <div className="h-0 after:clear-both after:table after:content-['']" />

            {!enableSidecar && tagsProps ? (
              <LandingPageContent
                as="section"
                maxWidth="medium"
                className="mb-l"
                data-testid="tags"
              >
                <Tags {...tagsProps} />
              </LandingPageContent>
            ) : null}
          </GatedContent>
          {!!(enableSidecar && !showDesktopSidecar && keyTopicsBlock) && (
            <LandingPageContent
              as="section"
              maxWidth="memo"
              className="mb-xl"
              data-testid="sidecar-keytopics-mobile"
            >
              <SidecarBlock
                border={false}
                gutters={false}
                block={keyTopicsBlock}
                blockIndex={0}
              />
            </LandingPageContent>
          )}
          {authorBioProps ? (
            <LandingPageContent
              as="section"
              maxWidth={enableSidecar ? "memo" : "medium"}
              className="mb-xl mt-l last:mb-0"
              data-testid="author-bios"
            >
              <ArticleAuthorStack>
                {authorBioProps.map((bioProps) => (
                  <AuthorBio
                    {...bioProps}
                    key={bioProps.href}
                    className="p-s"
                  />
                ))}
              </ArticleAuthorStack>
            </LandingPageContent>
          ) : null}
          {!!(enableSidecar && !showDesktopSidecar && wysiwygBlocks?.length) &&
            wysiwygBlocks.map((wysiwygBlock, idx) => (
              <LandingPageContent
                as="section"
                maxWidth="memo"
                className="mb-l"
                data-testid="sidecar-wysiwyg-mobile"
                key={`${wysiwygBlock.acfFcLayout}-${idx}`}
              >
                <SidecarBlock
                  border={false}
                  gutters={false}
                  block={wysiwygBlock}
                  blockIndex={0}
                />
              </LandingPageContent>
            ))}
          {!!(enableSidecar && !showDesktopSidecar && postListBlocks?.length) &&
            postListBlocks.map((postListBlock, idx) => (
              <LandingPageContent
                as="section"
                maxWidth="memo"
                className="mb-l last:mb-0"
                data-testid="sidecar-postlist-mobile"
                key={`${postListBlock.acfFcLayout}-${idx}`}
              >
                <SidecarBlock
                  border={false}
                  gutters={false}
                  block={postListBlock}
                  blockIndex={0}
                />
              </LandingPageContent>
            ))}
        </LandingPageContainer>

        {contentPromoProps ? (
          <LandingPageContent
            as="section"
            maxWidth="medium"
            className="print:hidden"
            data-testid="related-posts"
          >
            <ContentPromo
              headline={
                <Headline as="h3" level={3}>
                  Related content
                </Headline>
              }
              {...contentPromoProps}
            />
          </LandingPageContent>
        ) : null}
        {stickyNewsletterProps ? (
          <StickyNewsletterFlyout
            {...stickyNewsletterProps}
            closeFlyout={closeFlyout}
            isShowingFlyout={isShowingFlyout}
            defaultEmail={session?.user.email}
            status={status}
          />
        ) : null}
      </article>
    </>
  );
};
